/**
 * Therapist Session generation — CORE-05A, the weekly securing cycle.
 *
 * This module is the *validation and preview* half. The database is the truth:
 * TSESSION-021 requires the affected date/area Off counters to be LOCKED during
 * a commit, and a lock is not something a pure function can hold. So every rule
 * below is recomputed inside `secure_therapist_sessions()` in SQL — this copy
 * drives the client's CTA label ("Secure 5 sessions" vs "Submit Request") and
 * gives the Edge Function a second, independent statement of the same rule.
 * Validate twice, define once.
 *
 * Three things here are deliberate and easy to get wrong later.
 *
 * 1. **The securing cycle runs in the week BEFORE the service week.** Every time
 *    in TSESSION-007…011 is anchored to `weekStart − 7 … weekStart − 3` days, not
 *    to the service week itself. The chain proves it: Friday 7:00 PM locks the
 *    Final Therapist Count → Saturday 12:00 AM generates Rider Sessions (CORE-05B)
 *    → Sunday 12:00 AM opens them to all Riders → **Monday is the first service
 *    date**. Anchoring these to the service week would put the cutoff after the
 *    week it governs had already started.
 *
 * 2. **The Off limit is a per-area constant compared against a per-date count.**
 *    TSESSION-019 says "independently for each service date and Dispatch Area",
 *    but its input is `active_therapist_count`, which does not vary by date. So
 *    the *limit* is one number per area for the whole week; what varies per date
 *    is how much of it is already spent. CORE-05A §3.4 lists the inputs per
 *    date+area, which is where the per-area reading comes from.
 *
 * 3. **Nothing here computes a rank.** `therapistRank` arrives frozen from the
 *    cycle-entry snapshot (TSESSION-006/042). How a rank is *set* is undefined
 *    in the vault — see OQ-80.
 */

import { MANILA_UTC_OFFSET } from "./scheduled-slots.ts";

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/** Frozen at cycle entry — TSESSION-006. */
export type TherapistRank = "bronze" | "silver" | "gold" | "diamond";

/** §6.1 cycle states, minus `finalized` which is stored, never computed. */
export type TherapistCyclePhase = "not_open" | "priority_open" | "regular_open" | "securing_closed";

/** Why a proposal cannot take the normal path — TSESSION-020. */
export type ApprovalReason =
  | "below_rank_minimum"
  | "omits_saturday"
  | "omits_sunday"
  | "exceeds_off_limit";

/** §3.3 rank rules. Long-Distance eligibility is restated by TEND-058. */
export interface RankRule {
  /** Fewest Secured Sessions normal securing allows. */
  minimumSecured: number;
  /** Off Sessions normal securing allows — the complement of the minimum. */
  normalOffSessions: number;
  /** Monday 12:00 AM Priority Opening — TSESSION-007. */
  priorityOpening: boolean;
  /** TEND-058: Silver, Gold and Diamond only. */
  longDistanceEligible: boolean;
}

/** TSESSION-012…015, verbatim. */
export const RANK_RULES: Readonly<Record<TherapistRank, RankRule>> = {
  bronze: {
    minimumSecured: 6,
    normalOffSessions: 1,
    priorityOpening: false,
    longDistanceEligible: false,
  },
  silver: {
    minimumSecured: 6,
    normalOffSessions: 1,
    priorityOpening: false,
    longDistanceEligible: true,
  },
  gold: {
    minimumSecured: 5,
    normalOffSessions: 2,
    priorityOpening: false,
    longDistanceEligible: true,
  },
  diamond: {
    minimumSecured: 5,
    normalOffSessions: 2,
    priorityOpening: true,
    longDistanceEligible: true,
  },
} as const;

/**
 * Fewest Secured Sessions this rank may hold under normal securing — the
 * APPROVED value. A superadmin can change it in Settings (Luigi, 2026-09-11),
 * and the database's `therapist_rank_minimum()` reads that (0217). This stays
 * the default for a preview that could not load the saved one.
 */
export function rankMinimum(rank: TherapistRank): number {
  return RANK_RULES[rank].minimumSecured;
}

/** Auto Secure always produces exactly this many Secured dates — TSESSION-029. */
export const AUTO_SECURE_SECURED_COUNT = 6;

/** Every Therapist Session runs 2:00 PM → midnight, every hub — TSESSION-038/056. */
export const THERAPIST_SESSION_START_HOUR = 14;
export const THERAPIST_SESSION_DURATION_HOURS = 10;

/**
 * The normal Off Session limit for one date and Dispatch Area — TSESSION-019.
 *
 * ```
 * even N → N / 2
 * odd  N → max(0, floor(N / 2) − 1)
 * ```
 *
 * The `max(0, …)` is load-bearing and stated twice in the source ("The result can
 * never be negative"): at N = 1 the odd branch computes −1. A limit of 0 does not
 * forbid an Off date — it routes every Off proposal through Request for Approval.
 */
export function normalOffLimit(activeTherapistCount: number): number {
  if (!Number.isInteger(activeTherapistCount) || activeTherapistCount < 0) {
    throw new RangeError("activeTherapistCount must be a non-negative integer");
  }
  return activeTherapistCount % 2 === 0
    ? activeTherapistCount / 2
    : Math.max(0, Math.floor(activeTherapistCount / 2) - 1);
}

// ── calendar ────────────────────────────────────────────────────────────────
// Service dates are Manila calendar dates. Date-only arithmetic is done on a
// UTC-midnight instant on purpose: adding 86_400_000 ms to `YYYY-MM-DDT00:00:00Z`
// is exact calendar arithmetic with no zone in play, whereas doing the same at
// +08:00 invites an off-by-one every time someone reads the result back in UTC.

function dateOnlyMs(date: string, field: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new RangeError(`${field} must be YYYY-MM-DD`);
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(ms)) throw new RangeError(`${field} must be a real date`);
  return ms;
}

function toDateOnly(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Add whole days to a `YYYY-MM-DD`, returning a `YYYY-MM-DD`. */
export function addDays(date: string, days: number): string {
  return toDateOnly(dateOnlyMs(date, "date") + days * DAY_MS);
}

/**
 * The seven service dates, Monday through Sunday — §3.1.
 *
 * Rejects a non-Monday rather than silently snapping to one: a cycle keyed on a
 * Tuesday would put Saturday and Sunday in different weeks, and TSESSION-016
 * makes both mandatory.
 */
export function serviceWeekDates(weekStartDate: string): string[] {
  const ms = dateOnlyMs(weekStartDate, "weekStartDate");
  if (new Date(ms).getUTCDay() !== 1) {
    throw new RangeError("weekStartDate must be a Monday");
  }
  return Array.from({ length: 7 }, (_, i) => toDateOnly(ms + i * DAY_MS));
}

/** The fixed 2:00 PM → midnight window for one service date — TSESSION-038/056. */
export function therapistSessionWindow(serviceDate: string): {
  startsAt: string;
  endsAt: string;
} {
  dateOnlyMs(serviceDate, "serviceDate");
  const hh = String(THERAPIST_SESSION_START_HOUR).padStart(2, "0");
  const startMs = Date.parse(`${serviceDate}T${hh}:00:00${MANILA_UTC_OFFSET}`);
  return {
    startsAt: new Date(startMs).toISOString(),
    endsAt: new Date(startMs + THERAPIST_SESSION_DURATION_HOURS * HOUR_MS).toISOString(),
  };
}

/**
 * The Manila calendar date containing an instant — the "today" every service
 * date must be compared against.
 *
 * ⚠ `new Date().toISOString().slice(0, 10)` is the wrong answer and looks right
 * for two thirds of the day. Manila is UTC+8, so from 00:00 to 08:00 Manila the
 * UTC date is still YESTERDAY. Anything gating on "is this session today"
 * silently addresses the wrong service date for eight hours out of every
 * twenty-four — and does it overnight, when nobody is watching. `0068` is the
 * same defect in payroll's cron; this is its client-side twin.
 */
export function manilaDateOf(nowIso: string): string {
  const ms = Date.parse(nowIso);
  if (!Number.isFinite(ms)) throw new RangeError("nowIso must be a real instant");
  // Shift the instant onto Manila's clock, then read the calendar date off it in
  // UTC. Same trick as `dateOnlyMs` above, in the other direction, and it stays
  // inside this file's fixed-offset idiom (PH has had no DST since 1978).
  const offsetMs =
    Date.parse(`1970-01-01T00:00:00Z`) - Date.parse(`1970-01-01T00:00:00${MANILA_UTC_OFFSET}`);
  return new Date(ms + offsetMs).toISOString().slice(0, 10);
}

// ── daily operational state — TEND-008/013/014, Therapist Session State Machine
// § "Daily operational state" ───────────────────────────────────────────────
//
// This is the SECOND life of a Secured date. Everything above decides which
// dates she holds; this decides what one of them is doing today.
//
// ⚠ It is a PREVIEW, exactly like the money math. `start_therapist_session`
// re-derives all of it server-side (0085); a client that lied about the hour
// would be refused by the same rule expressed in SQL.

/** Start becomes available at 1:00 PM Manila on a Secured date — TEND-008. */
export const THERAPIST_START_WINDOW_HOUR = 13;

/**
 * Where one Secured date is in its day.
 *
 * `waiting` is PreStartWaiting — she has started early and is deliberately NOT
 * dispatchable yet (TEND-013). It is the state the rider side lacked and got
 * wrong, and the reason `available` is derived rather than flipped by a cron:
 * there is no 2:00 PM event to miss.
 */
export type TherapistSessionPhase =
  | "before_window"
  | "startable"
  | "waiting"
  | "available"
  | "ended";

/** The instant Start unlocks for a service date — 1:00 PM Manila. */
export function therapistStartWindowOpensAt(serviceDate: string): string {
  dateOnlyMs(serviceDate, "serviceDate");
  const hh = String(THERAPIST_START_WINDOW_HOUR).padStart(2, "0");
  return new Date(Date.parse(`${serviceDate}T${hh}:00:00${MANILA_UTC_OFFSET}`)).toISOString();
}

/**
 * The phase of one Secured date — the single expression both the button and the
 * dispatch predicate read.
 *
 * `startedAtIso` is null until she taps Start. Note that an unstarted date past
 * its window does NOT become `ended` before midnight: it stays `startable`, so a
 * therapist who is four hours late can still start and be marked Late Start
 * (TEND-014 gives no grace period, but it does not close the door either).
 */
export function therapistSessionPhase(
  nowIso: string,
  serviceDate: string,
  startedAtIso: string | null,
): TherapistSessionPhase {
  const now = Date.parse(nowIso);
  if (!Number.isFinite(now)) throw new RangeError("nowIso must be a real instant");
  const { startsAt, endsAt } = therapistSessionWindow(serviceDate);
  // Midnight closes the fixed period for everyone, started or not — TEND-016.
  // Work may CONTINUE past it (TEND-017), but no new session begins.
  if (now >= Date.parse(endsAt)) return "ended";
  if (startedAtIso === null) {
    return now >= Date.parse(therapistStartWindowOpensAt(serviceDate))
      ? "startable"
      : "before_window";
  }
  // Started. The 2:00 PM flip is a comparison, not an event.
  return now >= Date.parse(startsAt) ? "available" : "waiting";
}

/**
 * Late Start — TEND-014, no grace period.
 *
 * Strictly after 2:00 PM. Starting AT 2:00 PM exactly is on time: the rule says
 * "Start after exactly 2:00 PM records Late Start", and reading it as `>=` would
 * penalise the one therapist who started precisely on the bell.
 */
export function isTherapistLateStart(startedAtIso: string, serviceDate: string): boolean {
  const started = Date.parse(startedAtIso);
  if (!Number.isFinite(started)) throw new RangeError("startedAtIso must be a real instant");
  return started > Date.parse(therapistSessionWindow(serviceDate).startsAt);
}

/** An unmatched Trade expires at noon Manila on the service date — TSESSION-038. */
export function tradeExpiryAt(serviceDate: string): string {
  dateOnlyMs(serviceDate, "serviceDate");
  return new Date(Date.parse(`${serviceDate}T12:00:00${MANILA_UTC_OFFSET}`)).toISOString();
}

// ── the weekly timeline ─────────────────────────────────────────────────────

/** Every deadline in the cycle, as ISO instants — TSESSION-007…011. */
export interface CycleTimeline {
  /** Monday 12:00 AM, Diamond only. */
  priorityOpensAt: string;
  /** Tuesday 12:00 AM, everyone still unsecured. */
  regularOpensAt: string;
  /** Thursday 6:00 PM — absolute; unresolved requests Auto Decline. */
  approvalCutoffAt: string;
  /** Friday 7:00 AM — the 12-hour countdown starts. */
  countdownStartsAt: string;
  /** Friday 7:00 PM — securing closes, Auto Secure runs, counts lock. */
  securingCutoffAt: string;
}

function manilaInstant(date: string, hour: number, minute = 0): string {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return new Date(Date.parse(`${date}T${hh}:${mm}:00${MANILA_UTC_OFFSET}`)).toISOString();
}

/**
 * The five fixed deadlines for a service week — TSESSION-007…011, all Asia/Manila.
 *
 * Anchored to `weekStartDate − 7 … − 3`: the cycle that secures week W runs during
 * week W−1 (see the module header). The offsets are named rather than inlined so
 * the Monday-to-Friday shape of the securing week stays readable.
 */
export function cycleTimeline(weekStartDate: string): CycleTimeline {
  serviceWeekDates(weekStartDate); // validates it is a Monday
  const priorMonday = addDays(weekStartDate, -7);
  return {
    priorityOpensAt: manilaInstant(priorMonday, 0),
    regularOpensAt: manilaInstant(addDays(priorMonday, 1), 0),
    approvalCutoffAt: manilaInstant(addDays(priorMonday, 3), 18),
    countdownStartsAt: manilaInstant(addDays(priorMonday, 4), 7),
    securingCutoffAt: manilaInstant(addDays(priorMonday, 4), 19),
  };
}

/**
 * Which phase a cycle is in at an instant — §6.1.
 *
 * `finalized` is absent on purpose. It is not a function of the clock: it is set
 * only once `finalize_therapist_cycle()` has given every therapist seven final
 * date states (TSESSION-031). Deriving it from "past Friday 7:00 PM" would report
 * a cycle finalized while the Auto Secure loop was still running.
 */
export function cyclePhaseAt(nowIso: string, weekStartDate: string): TherapistCyclePhase {
  const now = Date.parse(nowIso);
  if (!Number.isFinite(now)) throw new RangeError("nowIso must be a valid ISO instant");
  const t = cycleTimeline(weekStartDate);
  if (now >= Date.parse(t.securingCutoffAt)) return "securing_closed";
  if (now >= Date.parse(t.regularOpensAt)) return "regular_open";
  if (now >= Date.parse(t.priorityOpensAt)) return "priority_open";
  return "not_open";
}

/** Whether the cycle is open to THIS therapist right now — §4.1.4/4.1.5. */
export function cycleOpenToRank(phase: TherapistCyclePhase, rank: TherapistRank): boolean {
  if (phase === "priority_open") return RANK_RULES[rank].priorityOpening;
  return phase === "regular_open";
}

/** New approval actions stop at Thursday 6:00 PM — TSESSION-009. */
export function approvalsOpenAt(nowIso: string, weekStartDate: string): boolean {
  return Date.parse(nowIso) < Date.parse(cycleTimeline(weekStartDate).approvalCutoffAt);
}

/**
 * Manual Auto Secure is offered only while all three hold — TSESSION-027.
 * `hasSecuredBatch` is the third and comes from the database, not the clock.
 */
export function manualAutoSecureAvailable(
  nowIso: string,
  weekStartDate: string,
  rank: TherapistRank,
  hasSecuredBatch: boolean,
): boolean {
  if (hasSecuredBatch) return false;
  const phase = cyclePhaseAt(nowIso, weekStartDate);
  return cycleOpenToRank(phase, rank);
}

/**
 * The decision deadline for a request submitted at `submittedAtIso` — TSESSION-023.
 *
 * "A request after an eligible day's 6:00 PM carries to the next day's 6:00 PM,
 * but Thursday 6:00 PM is absolute." Returns `null` when the absolute cutoff has
 * already passed — at that point no request may be submitted at all.
 *
 * ⚠ One boundary the rule does not address: a request submitted at *exactly*
 * 6:00 PM. Read literally it would get that same instant as its deadline — a
 * zero-second decision window and an immediate Auto Decline. This takes the next
 * 6:00 PM **strictly after** submission instead, so an on-the-tick submission
 * gets a real day. Flipping to the literal reading is one comparison operator.
 */
export function approvalDeadlineFor(submittedAtIso: string, weekStartDate: string): string | null {
  const submitted = Date.parse(submittedAtIso);
  if (!Number.isFinite(submitted))
    throw new RangeError("submittedAtIso must be a valid ISO instant");
  const absolute = Date.parse(cycleTimeline(weekStartDate).approvalCutoffAt);
  if (submitted >= absolute) return null;

  // Walk 6:00 PM Manila forward from the submission day until one is strictly
  // later than the submission, then clamp to Thursday. At most two iterations.
  const priorMonday = addDays(weekStartDate, -7);
  for (let day = 0; day <= 3; day += 1) {
    const candidate = Date.parse(manilaInstant(addDays(priorMonday, day), 18));
    if (candidate > submitted) return new Date(Math.min(candidate, absolute)).toISOString();
  }
  return new Date(absolute).toISOString();
}

// ── proposal validation ─────────────────────────────────────────────────────

/** What the caller knows about one date's Off pressure — §3.4. */
export interface OffLimitContext {
  serviceDate: string;
  /** Off Sessions already committed for this date and Dispatch Area. */
  currentOffCount: number;
  /** `normalOffLimit(activeTherapistCount)` for this Dispatch Area. */
  offLimit: number;
}

export interface ProposalInput {
  /** Monday of the service week. */
  weekStartDate: string;
  /** The dates the therapist wants Secured. The other of the seven become Off. */
  selectedDates: readonly string[];
  /** Frozen at cycle entry — never the live `staff` value. */
  rank: TherapistRank;
  /** One entry per service date. */
  offContext: readonly OffLimitContext[];
  /**
   * The minimum for `rank` as the database holds it now, which a superadmin can
   * edit (0217). Omitted or not a whole number, the approved `rankMinimum`.
   * The server re-assesses with its own reading, so a stale value here can only
   * mislabel the button — it can never commit a week.
   */
  minimumSecured?: number;
}

export interface ProposalAssessment {
  /** All seven dates, Monday first. */
  weekDates: string[];
  securedDates: string[];
  offDates: string[];
  /** Empty when normal securing may commit this proposal as-is. */
  reasons: ApprovalReason[];
  /** Off dates that would push their date past the normal limit. */
  overLimitDates: string[];
  requiresApproval: boolean;
}

/**
 * Assess a seven-date proposal against TSESSION-020 — the whole approval gate.
 *
 * Returns *every* reason rather than the first, because the therapist should see
 * one screen explaining why a submission needs approval, not a sequence of them.
 *
 * Note what this does NOT do: it never rejects. A proposal below the rank minimum
 * is not an error, it is a Request-for-Approval route (TSG-002). The only hard
 * failure is a malformed proposal — dates outside the week, or duplicates — which
 * is a bug in the caller, not a therapist's choice.
 */
export function assessProposal(input: ProposalInput): ProposalAssessment {
  const weekDates = serviceWeekDates(input.weekStartDate);
  const week = new Set(weekDates);

  const selected = new Set(input.selectedDates);
  if (selected.size !== input.selectedDates.length) {
    throw new RangeError("selectedDates must not contain duplicates");
  }
  for (const d of selected) {
    if (!week.has(d)) throw new RangeError(`${d} is not in the service week`);
  }

  const securedDates = weekDates.filter((d) => selected.has(d));
  const offDates = weekDates.filter((d) => !selected.has(d));

  // Saturday and Sunday are the last two entries of a Monday-first week.
  const [saturday, sunday] = [weekDates[5]!, weekDates[6]!];

  const byDate = new Map(input.offContext.map((c) => [c.serviceDate, c]));
  const overLimitDates = offDates.filter((d) => {
    const ctx = byDate.get(d);
    // An absent context cannot be treated as "within limit" — that would let a
    // caller skip the check by omitting the date. Missing means unknown, and
    // unknown routes to approval.
    if (!ctx) return true;
    return ctx.currentOffCount + 1 > ctx.offLimit;
  });

  const reasons: ApprovalReason[] = [];
  const minimum =
    input.minimumSecured !== undefined && Number.isInteger(input.minimumSecured)
      ? input.minimumSecured
      : rankMinimum(input.rank);
  if (securedDates.length < minimum) reasons.push("below_rank_minimum");
  if (!selected.has(saturday)) reasons.push("omits_saturday");
  if (!selected.has(sunday)) reasons.push("omits_sunday");
  if (overLimitDates.length > 0) reasons.push("exceeds_off_limit");

  return {
    weekDates,
    securedDates,
    offDates,
    reasons,
    overLimitDates,
    requiresApproval: reasons.length > 0,
  };
}

// ── Auto Secure ─────────────────────────────────────────────────────────────

/**
 * Which weekday becomes the Off Session — TSESSION-030.
 *
 * "Among Monday-Friday, Auto Secure makes the weekday with the highest
 * already-secured count in that Dispatch Area the Off Session. A tie selects the
 * earliest weekday in Monday-Friday order as Off."
 *
 * Returns a 0-based index into Monday…Friday. A strict `>` comparison walking
 * forward is what makes the tie resolve to the earliest — `>=` would silently
 * pick the latest and TSG-006 would fail.
 */
export function autoSecureOffWeekday(weekdaySecuredCounts: readonly number[]): number {
  if (weekdaySecuredCounts.length !== 5) {
    throw new RangeError("weekdaySecuredCounts must hold exactly Monday…Friday");
  }
  let best = 0;
  for (let i = 1; i < 5; i += 1) {
    if (weekdaySecuredCounts[i]! > weekdaySecuredCounts[best]!) best = i;
  }
  return best;
}

export interface AutoSecureResult {
  securedDates: string[];
  offDate: string;
}

/**
 * The standard Auto Secure batch — TSESSION-029/030, §4.8.
 *
 * Always exactly six Secured and one Off, "regardless of rank" — a Gold therapist
 * whose normal minimum is five still receives six. That is stated by TSESSION-029
 * and restated in the client's own intake note, so it is not a rounding artefact.
 *
 * ⚠ This deliberately does NOT consult the Off limit. TSESSION-029/030 select the
 * Off weekday purely by highest-secured count and never mention TSESSION-019, so
 * a large unsecured cohort can push a date past a limit manual securing enforced
 * all week. Following the rules as written — recorded as OQ-82.
 */
export function autoSecureDates(
  weekStartDate: string,
  weekdaySecuredCounts: readonly number[],
): AutoSecureResult {
  const weekDates = serviceWeekDates(weekStartDate);
  const offDate = weekDates[autoSecureOffWeekday(weekdaySecuredCounts)]!;
  const securedDates = weekDates.filter((d) => d !== offDate);
  if (securedDates.length !== AUTO_SECURE_SECURED_COUNT) {
    // Unreachable while the week holds seven distinct dates, which
    // `serviceWeekDates` guarantees. Asserted rather than assumed: a batch of
    // any other size is not an Auto Secure batch.
    throw new RangeError(`Auto Secure produced ${securedDates.length} secured dates, expected 6`);
  }
  return { securedDates, offDate };
}

/**
 * The Final Therapist Count for one date and Dispatch Area — TSESSION-032.
 *
 * Distinct therapists holding a **Secured** date; Off Sessions excluded. Trivial
 * arithmetic, but it exists so the count has one definition the tests can point
 * at — the SQL lock and this must agree, and "distinct" is the word that makes a
 * duplicated row harmless instead of inflating a hub's rider supply.
 */
export function finalTherapistCount(
  secured: readonly { therapistId: string; state: "secured" | "off" }[],
): number {
  return new Set(secured.filter((s) => s.state === "secured").map((s) => s.therapistId)).size;
}
